import { useEffect, useRef, useState } from 'react';
import { ExternalLink, ChevronLeft, Check } from 'lucide-react';
import { capturePublicApi } from '../api/captureApi';
import type { InviteResolution } from '../api/captureApi';
import { googleLinkHint } from './googleLinkHint';
import styles from '../pages/ReviewCapturePage.module.css';

// ─── Google post button (optionally copies draft → opens GBP) ───────────────

/**
 * Tapping the button hands the patient off to Google in another tab — we never
 * see whether they went through with it. So once they've been sent over, this
 * asks them directly. Only "Yes, I posted it" marks the review as published;
 * until then the practice's dashboard doesn't show the text at all.
 *
 * The copy-and-paste walkthrough used to live here, gating the first tap
 * behind an "instructions" stage. It's now shown earlier, on
 * CaptureExpectationScreen, before the patient ever sees a button that could
 * read as the end of the flow — so the first tap here goes straight to
 * Google. "I got stuck" re-shows the same steps inline, for anyone who needs
 * the reminder after they've already left.
 */
type PostStage = 'idle' | 'handed_off' | 'confirmed';

export function GooglePostBtn({
  href,
  draft,
  shortCode,
  rating,
  copyToClipboard,
  patientName,
  patientContact,
  disabled,
  disabledHint,
  hint,
  variant = 'primary',
}: {
  href: string;
  draft: string;
  shortCode: string;
  rating: number | null;
  copyToClipboard: boolean;
  /** Sent along with the hand-off — required server-side below 5 stars. */
  patientName?: string;
  patientContact?: string;
  /** Blocks the initial tap (e.g. contact fields not filled yet below 5 stars). */
  disabled?: boolean;
  disabledHint?: string;
  /** Replaces the default line under the button (used when Google opens Maps, not the review box). */
  hint?: string;
  /**
   * 'primary' (default): the only action on the screen (5-star). 'secondary':
   * shares the screen with an equally- or more-prominent alternative action
   * (below 5 stars, "Send privately" is primary) — see CapturePrivateScreen.
   */
  variant?: 'primary' | 'secondary';
}) {
  const [stage, setStage] = useState<PostStage>('idle');
  // Re-shows the walkthrough inline on the handed_off stage, for a patient who
  // gets there and realizes they need the steps again.
  const [showSteps, setShowSteps] = useState(false);
  const ctaClass = variant === 'secondary' ? styles.ctaGoogleSecondary : styles.ctaGoogle;

  function copyDraft() {
    if (draft && copyToClipboard) {
      navigator.clipboard
        .writeText(draft)
        .catch((err: unknown) => console.warn('Clipboard write failed', err));
    }
  }

  // Copies the draft, opens Google in a new tab, and records the hand-off.
  // What to do once Google opens (tap stars, press-hold, paste) was already
  // covered on CaptureExpectationScreen, so this tap goes straight through
  // instead of gating behind a second "instructions" screen.
  function handlePostTap() {
    copyDraft();
    capturePublicApi
      .markComplete(shortCode, draft, rating ?? undefined, patientName, patientContact)
      .catch((err: unknown) => console.debug('markComplete tracking failed', err));
    window.open(href, '_blank', 'noopener,noreferrer');
    setStage('handed_off');
  }

  // Re-copy on every re-open so the clipboard is at its freshest — that is
  // what makes phone keyboards surface their tap-to-paste suggestion when the
  // review box is focused, sparing the press-and-hold.
  function handleOpenGoogleAgain() {
    copyDraft();
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  function handleConfirm() {
    setStage('confirmed');
    capturePublicApi
      .confirmPosted(shortCode)
      .catch((err: unknown) => console.debug('confirmPosted failed', err));
  }

  if (stage === 'confirmed') {
    return (
      <div className={styles.privateDone}>
        <Check size={18} aria-hidden /> Thank you — we really appreciate you sharing that.
      </div>
    );
  }

  if (stage === 'handed_off') {
    return (
      <div className={styles.googleBtnWrap}>
        <button type="button" className={ctaClass} onClick={handleOpenGoogleAgain}>
          <ExternalLink size={16} aria-hidden /> Open Google again
        </button>
        <div className={styles.confirmPrompt}>
          <p className={styles.confirmQuestion}>Did your review make it onto Google?</p>
          <button type="button" className={styles.btnConfirm} onClick={handleConfirm}>
            <Check size={16} aria-hidden /> Yes, I posted it
          </button>
          {copyToClipboard && (
            <button
              type="button"
              className={styles.clearDraftBtn}
              onClick={() => setShowSteps((v) => !v)}
            >
              {showSteps ? 'Hide the steps' : 'I got stuck — show me how again'}
            </button>
          )}
          {copyToClipboard && showSteps && (
            <ol className={styles.postSteps}>
              <li className={styles.postStep}>
                <span className={styles.postStepNum}>1</span> Tap the stars ★★★★★
              </li>
              <li className={styles.postStep}>
                <span className={styles.postStepNum}>2</span> Press and hold inside the big text box
              </li>
              <li className={styles.postStep}>
                <span className={styles.postStepNum}>3</span> Tap <strong>Paste</strong>
              </li>
            </ol>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.googleBtnWrap}>
      <button type="button" className={ctaClass} onClick={handlePostTap} disabled={disabled}>
        <ExternalLink size={16} aria-hidden />{' '}
        {copyToClipboard ? 'Open Google to paste the review' : 'Post to Google'}
      </button>
      <p className={styles.copiedHintAlert}>
        {disabled
          ? disabledHint
          : hint
            ? hint
            : copyToClipboard
              ? 'We copy the review for you — just paste it in when Google opens.'
              : "You'll be taken to Google to leave your review."}
      </p>
    </div>
  );
}

// ─── Share screen ─────────────────────────────────────────────────────────
//
// 5-star only. A 5-star patient sees just the Google post path here — no
// private-feedback alternative. Offering an outlet that skips the public
// review only matters for compliance on the LOW-rating side (see
// CapturePrivateScreen, which shows both options together, non-sequentially,
// for exactly that reason); simplifying the happiest patients straight to
// the public review isn't the pattern FTC review-gating rules are aimed at.

export function CaptureShareScreen({
  resolution,
  shortCode,
  rating,
  draft,
  draftLoading,
  onChangeDraft,
  copyToClipboard,
  onBack,
  patientInputs = [],
  patientFreeText = '',
}: {
  resolution: InviteResolution;
  shortCode: string;
  rating: number | null;
  draft: string;
  draftLoading: boolean;
  onChangeDraft: (v: string) => void;
  copyToClipboard: boolean;
  onBack: () => void;
  /** Plain-English reminders of what the patient tapped — see the empty-draft state below. */
  patientInputs?: string[];
  /** The patient's own free-typed words, if given — used to seed the empty-draft textarea. */
  patientFreeText?: string;
}) {
  const draftAreaRef = useRef<HTMLTextAreaElement>(null);
  // AI generation tried and came back with nothing (every candidate across
  // every attempt failed) — never a hardcoded review, see capture.freshness.ts.
  // The patient writes it themselves instead, starting from their own words
  // and a reminder of what they already told us, so they never have to
  // re-remember or retype anything.
  const patientAuthoring = !draftLoading && !draft;

  useEffect(() => {
    const el = draftAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft, draftLoading]);

  // Only seed the box once, the first time this state is reached — a patient
  // who then clears it on purpose shouldn't have their own words reappear.
  // The ref (not the dependency array) is what makes this idempotent, since
  // onChangeDraft is a fresh function identity on every parent render.
  const seededRef = useRef(false);
  useEffect(() => {
    if (patientAuthoring && patientFreeText && !seededRef.current) {
      seededRef.current = true;
      onChangeDraft(patientFreeText);
    }
  }, [patientAuthoring, patientFreeText, onChangeDraft]);

  return (
    <div className={styles.card}>
      {/* Assembling the draft takes a few seconds. Say what is happening and that
          it will be editable, so the wait reads as progress rather than a stall. */}
      {draftLoading && (
        <>
          <div className={styles.question}>Writing your review...</div>
          <p className={styles.sub}>
            Hang tight while we turn your answers into a review. You&apos;ll be able to edit every
            word of it once it appears.
          </p>
        </>
      )}
      {!draftLoading && draft && (
        <>
          <div className={styles.question}>Your review</div>
          <p className={styles.sub}>You can edit this generated review or start fresh.</p>
        </>
      )}
      {patientAuthoring && (
        <>
          <div className={styles.question}>Let&apos;s write this one together</div>
          <p className={styles.sub}>
            We couldn&apos;t quite put your review into words this time — but you can, in just a
            couple of sentences. Here&apos;s what you told us:
          </p>
          {patientInputs.length > 0 && (
            <ul className={styles.reminderList} aria-label="What you told us">
              {patientInputs.map((line) => (
                <li key={line} className={styles.reminderItem}>
                  {line}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {draftLoading ? (
        <div className={styles.draftSkeleton} aria-label="Assembling your draft…">
          <div className={styles.draftSkeletonLine} />
          <div className={styles.draftSkeletonLine} />
          <div className={styles.draftSkeletonLine} />
          <div className={styles.draftSkeletonLineLast} />
        </div>
      ) : (
        <textarea
          ref={draftAreaRef}
          className={styles.draftArea}
          value={draft}
          onChange={(e) => onChangeDraft(e.target.value)}
          rows={5}
          placeholder="Write your review here…"
        />
      )}
      {!draftLoading && resolution.isTest && resolution.googleLinkKind === 'maps_search' && (
        <p className={styles.testBadge}>
          Test: this location has no Google review link connected, so patients would land on a
          Google Maps search. Connect Google Business Profile to send them straight to the review
          box.
        </p>
      )}
      {!draftLoading && resolution.gbpDeepLink && (
        <GooglePostBtn
          href={resolution.gbpDeepLink}
          draft={draft}
          shortCode={shortCode}
          rating={rating}
          copyToClipboard={copyToClipboard}
          hint={googleLinkHint(resolution, copyToClipboard)}
        />
      )}

      <button type="button" className={styles.backBtn} onClick={onBack}>
        <ChevronLeft size={16} aria-hidden /> Back
      </button>
    </div>
  );
}
